import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEnvelope, FaLock, FaUser, FaPhone, FaIdCard, FaSpinner, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../services/api';

const PartnerRegister: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    cpfCnpj: '',
    telefone: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: dados pessoais, 2: dados de acesso, 3: confirmação
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatCnpj = (value: string) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // Formata CNPJ: 00.000.000/0000-00
    return numbers
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .slice(0, 18); // Limita o tamanho
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 10) {
      // Formato: (00) 0000-0000
      return numbers
        .replace(/^(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    } else {
      // Formato: (00) 00000-0000
      return numbers
        .replace(/^(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2')
        .slice(0, 15);
    }
  };

  const handleCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCnpj(e.target.value);
    setFormData(prev => ({
      ...prev,
      cpfCnpj: formatted
    }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setFormData(prev => ({
      ...prev,
      telefone: formatted
    }));
  };

  const validateStep1 = () => {
    if (!formData.fullName.trim()) {
      toast.error('Por favor, informe seu nome completo');
      return false;
    }
    if (!formData.cpfCnpj.trim()) {
      toast.error('Por favor, informe o CPF ou CNPJ');
      return false;
    }
    if (!formData.telefone.trim()) {
      toast.error('Por favor, informe seu telefone');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.email.trim()) {
      toast.error('Por favor, informe o e-mail');
      return false;
    }
    if (!formData.email.includes('@')) {
      toast.error('Por favor, informe um e-mail válido');
      return false;
    }
    if (!formData.password) {
      toast.error('Por favor, informe a senha');
      return false;
    }
    if (formData.password.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handlePreviousStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep1() || !validateStep2()) {
      return;
    }

    setIsLoading(true);

    try {
      const registerData = {
        fullName: formData.fullName,
        email: formData.email,
        password: formData.password,
        cpfCnpj: formData.cpfCnpj,
        telefone: formData.telefone,
        nivelAcesso: 'PARCEIRO'
      };

      await api.post('/auth/register', registerData);
      
      toast.success('Cadastro realizado com sucesso! Redirecionando para o login...');
      
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Erro ao realizar cadastro';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen flex items-center justify-center bg-gradient-to-br from-[#0f1419] via-[#1a2332] to-[#253547] relative overflow-hidden p-4">
      <div className="absolute top-0 left-0 right-0 bottom-0 overflow-hidden">
        <div className="absolute w-[600px] h-[600px] -top-[200px] -right-[200px] rounded-full bg-gradient-to-br from-[#089453]/10 to-[#84CC34]/5 animate-[float_20s_infinite_ease-in-out]"></div>
        <div className="absolute w-[400px] h-[400px] -bottom-[100px] -left-[100px] rounded-full bg-gradient-to-br from-[#089453]/10 to-[#84CC34]/5 animate-[float_25s_infinite_ease-in-out_reverse]"></div>
        <div className="absolute w-[300px] h-[300px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-[#089453]/10 to-[#84CC34]/5 animate-[pulse-bg_10s_infinite_ease-in-out]"></div>
      </div>

      <div className="bg-[#1a2332]/95 backdrop-blur-[20px] rounded-3xl p-6 max-w-[600px] w-full h-[calc(100vh-2rem)] flex flex-col shadow-[0_20px_60px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.1)] relative z-10 animate-[slideUp_0.5s_ease-out]">
        <div className="text-center mb-4 flex-shrink-0">
          <div className="mb-2 flex justify-center">
            <img src="/logo.png" alt="AbasteceMais" className="w-12 h-12 object-contain drop-shadow-[0_4px_12px_rgba(8,148,83,0.4)]" />
          </div>
          <h1 className="text-white text-[24px] font-bold m-0 mb-1 [text-shadow:0_2px_8px_rgba(0,0,0,0.3)]">Cadastro de Parceiro</h1>
          <p className="text-white/70 text-sm m-0">Junte-se à nossa rede de postos</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-4 px-4 flex-shrink-0">
          <div className={`flex flex-col items-center gap-1 flex-1 max-w-[100px] ${step >= 1 ? 'active' : ''}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 ${step >= 1 ? 'bg-gradient-to-br from-[#089453] to-[#84cc34] border-[#089453] text-white shadow-[0_4px_16px_rgba(8,148,83,0.4)]' : 'bg-white/10 border-2 border-white/20 text-white/50'} ${step > 1 ? 'bg-[#089453] border-[#089453] text-white' : ''}`}>
              {step > 1 ? <FaCheckCircle className="text-xs" /> : '1'}
            </div>
            <span className={`text-[10px] text-center transition-colors duration-300 ${step >= 1 ? 'text-[#84cc34] font-semibold' : 'text-white/50'}`}>Dados Pessoais</span>
          </div>
          <div className={`h-0.5 flex-1 mx-2 mb-5 transition-all duration-300 ${step > 1 ? 'bg-gradient-to-r from-[#089453] to-[#84cc34]' : 'bg-white/10'}`}></div>
          <div className={`flex flex-col items-center gap-1 flex-1 max-w-[100px] ${step >= 2 ? 'active' : ''}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 ${step >= 2 ? 'bg-gradient-to-br from-[#089453] to-[#84cc34] border-[#089453] text-white shadow-[0_4px_16px_rgba(8,148,83,0.4)]' : 'bg-white/10 border-2 border-white/20 text-white/50'} ${step > 2 ? 'bg-[#089453] border-[#089453] text-white' : ''}`}>
              {step > 2 ? <FaCheckCircle className="text-xs" /> : '2'}
            </div>
            <span className={`text-[10px] text-center transition-colors duration-300 ${step >= 2 ? 'text-[#84cc34] font-semibold' : 'text-white/50'}`}>Acesso</span>
          </div>
          <div className={`h-0.5 flex-1 mx-2 mb-5 transition-all duration-300 ${step > 2 ? 'bg-gradient-to-r from-[#089453] to-[#84cc34]' : 'bg-white/10'}`}></div>
          <div className={`flex flex-col items-center gap-1 flex-1 max-w-[100px] ${step === 3 ? 'active' : ''}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 ${step === 3 ? 'bg-gradient-to-br from-[#089453] to-[#84cc34] border-[#089453] text-white shadow-[0_4px_16px_rgba(8,148,83,0.4)]' : 'bg-white/10 border-2 border-white/20 text-white/50'}`}>
              3
            </div>
            <span className={`text-[10px] text-center transition-colors duration-300 ${step === 3 ? 'text-[#84cc34] font-semibold' : 'text-white/50'}`}>Confirmação</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col justify-between min-h-0">
          {/* Step 1: Dados Pessoais */}
          {step === 1 && (
            <div className="animate-[fadeIn_0.4s_ease-out] flex-1">
              <div className="mb-3">
                <label htmlFor="fullName" className="block text-white/90 font-medium mb-1.5 text-sm">Nome Completo *</label>
                <div className="relative flex items-center">
                  <FaUser className="absolute left-4 text-[#84CC34]/70 text-base pointer-events-none" />
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Nome completo ou razão social"
                    disabled={isLoading}
                    className="w-full py-2.5 pl-12 pr-4 bg-white/5 border-2 border-white/10 rounded-xl text-white text-sm transition-all duration-300 placeholder:text-white/40 focus:outline-none focus:border-[#84cc34] focus:bg-white/[0.08] focus:shadow-[0_0_0_4px_rgba(132,204,52,0.1)] disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="cpfCnpj" className="block text-white/90 font-medium mb-1.5 text-sm">CNPJ *</label>
                <div className="relative flex items-center">
                  <FaIdCard className="absolute left-4 text-[#84CC34]/70 text-base pointer-events-none" />
                  <input
                    type="text"
                    id="cpfCnpj"
                    name="cpfCnpj"
                    value={formData.cpfCnpj}
                    onChange={handleCnpjChange}
                    placeholder="00.000.000/0000-00"
                    disabled={isLoading}
                    className="w-full py-2.5 pl-12 pr-4 bg-white/5 border-2 border-white/10 rounded-xl text-white text-sm transition-all duration-300 placeholder:text-white/40 focus:outline-none focus:border-[#84cc34] focus:bg-white/[0.08] focus:shadow-[0_0_0_4px_rgba(132,204,52,0.1)] disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="telefone" className="block text-white/90 font-medium mb-1.5 text-sm">Telefone *</label>
                <div className="relative flex items-center">
                  <FaPhone className="absolute left-4 text-[#84CC34]/70 text-base pointer-events-none" />
                  <input
                    type="text"
                    id="telefone"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handlePhoneChange}
                    placeholder="(00) 00000-0000"
                    disabled={isLoading}
                    className="w-full py-2.5 pl-12 pr-4 bg-white/5 border-2 border-white/10 rounded-xl text-white text-sm transition-all duration-300 placeholder:text-white/40 focus:outline-none focus:border-[#84cc34] focus:bg-white/[0.08] focus:shadow-[0_0_0_4px_rgba(132,204,52,0.1)] disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Dados de Acesso */}
          {step === 2 && (
            <div className="animate-[fadeIn_0.4s_ease-out] flex-1">
              <div className="mb-3">
                <label htmlFor="email" className="block text-white/90 font-medium mb-1.5 text-sm">E-mail *</label>
                <div className="relative flex items-center">
                  <FaEnvelope className="absolute left-4 text-[#84CC34]/70 text-base pointer-events-none" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="seu@email.com"
                    disabled={isLoading}
                    className="w-full py-2.5 pl-12 pr-4 bg-white/5 border-2 border-white/10 rounded-xl text-white text-sm transition-all duration-300 placeholder:text-white/40 focus:outline-none focus:border-[#84cc34] focus:bg-white/[0.08] focus:shadow-[0_0_0_4px_rgba(132,204,52,0.1)] disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="password" className="block text-white/90 font-medium mb-1.5 text-sm">Senha *</label>
                <div className="relative flex items-center">
                  <FaLock className="absolute left-4 text-[#84CC34]/70 text-base pointer-events-none" />
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Mínimo 6 caracteres"
                    disabled={isLoading}
                    className="w-full py-2.5 pl-12 pr-4 bg-white/5 border-2 border-white/10 rounded-xl text-white text-sm transition-all duration-300 placeholder:text-white/40 focus:outline-none focus:border-[#84cc34] focus:bg-white/[0.08] focus:shadow-[0_0_0_4px_rgba(132,204,52,0.1)] disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="confirmPassword" className="block text-white/90 font-medium mb-1.5 text-sm">Confirmar Senha *</label>
                <div className="relative flex items-center">
                  <FaLock className="absolute left-4 text-[#84CC34]/70 text-base pointer-events-none" />
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Digite a senha novamente"
                    disabled={isLoading}
                    className="w-full py-2.5 pl-12 pr-4 bg-white/5 border-2 border-white/10 rounded-xl text-white text-sm transition-all duration-300 placeholder:text-white/40 focus:outline-none focus:border-[#84cc34] focus:bg-white/[0.08] focus:shadow-[0_0_0_4px_rgba(132,204,52,0.1)] disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Confirmação */}
          {step === 3 && (
            <div className="animate-[fadeIn_0.4s_ease-out] text-center flex-1">
              <div className="mb-3">
                <FaCheckCircle className="text-4xl text-[#84cc34] mb-2 animate-[scaleIn_0.5s_ease-out] mx-auto" />
                <h3 className="text-white text-xl m-0">Confirme seus dados</h3>
              </div>

              <div className="bg-black/20 rounded-2xl p-3 mb-3 text-left">
                <div className="flex justify-between items-center py-2 border-b border-white/10 last:border-b-0">
                  <strong className="text-white/70 font-medium text-xs">Nome:</strong>
                  <span className="text-white font-semibold text-xs text-right">{formData.fullName}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/10 last:border-b-0">
                  <strong className="text-white/70 font-medium text-xs">CNPJ:</strong>
                  <span className="text-white font-semibold text-xs text-right">{formData.cpfCnpj}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/10 last:border-b-0">
                  <strong className="text-white/70 font-medium text-xs">Telefone:</strong>
                  <span className="text-white font-semibold text-xs text-right">{formData.telefone}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/10 last:border-b-0">
                  <strong className="text-white/70 font-medium text-xs">E-mail:</strong>
                  <span className="text-white font-semibold text-xs text-right">{formData.email}</span>
                </div>
              </div>

              <div className="mt-3 p-2.5 bg-[#84CC34]/10 rounded-xl border border-[#84CC34]/20">
                <p className="text-white/80 text-[11px] m-0 leading-relaxed">
                  Ao se cadastrar, você concorda com nossos{' '}
                  <button type="button" className="bg-transparent border-none text-[#84cc34] font-semibold cursor-pointer p-0 text-[11px] transition-colors duration-200 hover:text-[#089453] hover:underline" onClick={() => {}}>termos de uso</button> e{' '}
                  <button type="button" className="bg-transparent border-none text-[#84cc34] font-semibold cursor-pointer p-0 text-[11px] transition-colors duration-200 hover:text-[#089453] hover:underline" onClick={() => navigate('/politica-privacidade')}>política de privacidade</button>.
                </p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3 mt-4 flex-shrink-0">
            {step > 1 && (
              <button
                type="button"
                className="flex-1 py-2.5 px-4 border-2 border-white/20 rounded-xl text-sm font-semibold cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 bg-white/10 text-white hover:enabled:bg-white/15 hover:enabled:border-white/30 hover:enabled:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handlePreviousStep}
                disabled={isLoading}
              >
                Voltar
              </button>
            )}

            {step < 3 ? (
              <button
                type="button"
                className="flex-1 py-2.5 px-4 border-none rounded-xl text-sm font-semibold cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 bg-gradient-to-br from-[#089453] to-[#84cc34] text-white shadow-[0_4px_16px_rgba(8,148,83,0.3)] hover:enabled:shadow-[0_6px_24px_rgba(8,148,83,0.4)] hover:enabled:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleNextStep}
                disabled={isLoading}
              >
                Próximo
              </button>
            ) : (
              <button
                type="submit"
                className="flex-1 py-2.5 px-4 border-none rounded-xl text-sm font-semibold cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 bg-gradient-to-br from-[#089453] to-[#84cc34] text-white shadow-[0_4px_16px_rgba(8,148,83,0.3)] hover:enabled:shadow-[0_6px_24px_rgba(8,148,83,0.4)] hover:enabled:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Cadastrando...
                  </>
                ) : (
                  'Finalizar Cadastro'
                )}
              </button>
            )}
          </div>
        </form>

        <div className="text-center mt-3 pt-3 border-t border-white/10 flex-shrink-0">
          <p className="text-white/70 text-xs m-0">
            Já possui uma conta?{' '}
            <button
              onClick={() => navigate('/login')}
              className="bg-transparent border-none text-[#84cc34] font-semibold cursor-pointer transition-colors duration-200 p-0 text-xs hover:enabled:text-[#089453] hover:enabled:underline disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              Fazer login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PartnerRegister;
